import { useCallback, useMemo, useState } from 'react';
import { ActionType, ComponentType } from '@cbhq/client-analytics';
import { CoinbaseJanusSecondfactorSecondFactorType } from '@cbhq/identity-api-hooks/two-factor/types';
import { useGetListAllSecondFactorsCallback } from '@cbhq/identity-api-hooks/two-factor-management/fetch';
import {
  CoinbaseJanusManagementListAllSecondFactorFormFactor,
  CoinbaseJanusSecondfactorsSecondFactorsStatus,
} from '@cbhq/identity-api-hooks/two-factor-management/types';
import { TwoFactorTargetEnv } from '@cbhq/identity-shared/types';

import { useClientProps } from '../providers/ClientPropsProvider';

import { useAnalytics } from './useAnalytics';
import { useEffectOnce } from './useEffectOnce';
import { useGetIsBusinessUser } from './useGetIsBusinessUser';

export const PASSKEY_UPSELL_KEY = 'passkey_upsell';

function isStoredDateOlderThan30Days(storedTime: string) {
  // Convert the stored string back to a number
  const storedTimestamp = Number(storedTime);

  // Calculate 30 days in milliseconds
  const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;

  // Get the current timestamp
  const now = Date.now();

  // Compare the difference
  return now - storedTimestamp > thirtyDaysInMs;
}

export function isPasskeyEnabled(
  formFactors: CoinbaseJanusManagementListAllSecondFactorFormFactor[],
) {
  const passkey = formFactors.find(
    (factor) => factor.secondFactorType === CoinbaseJanusSecondfactorSecondFactorType.PASSKEY,
  );
  return (
    passkey?.status === CoinbaseJanusSecondfactorsSecondFactorsStatus.ENABLED ||
    Boolean(passkey?.records?.length)
  );
}

export function isSecurityKeyEnabled(
  formFactors: CoinbaseJanusManagementListAllSecondFactorFormFactor[],
) {
  const u2f = formFactors.find(
    (factor) => factor.secondFactorType === CoinbaseJanusSecondfactorSecondFactorType.U2F,
  );
  return u2f?.status === CoinbaseJanusSecondfactorsSecondFactorsStatus.ENABLED;
}

export function useShowPasskeyGuideTourModel() {
  const { renderPasskeyGuideTourModal, target } = useClientProps();
  const { logEvent } = useAnalytics();
  const getListEnabledSecondFactors = useGetListAllSecondFactorsCallback();
  const getIsBusinessUser = useGetIsBusinessUser();

  const [show, setShow] = useState(false);

  const checkIfPasskeyGuideTourShouldBeShown = useCallback(async () => {
    const storedTime = localStorage.getItem(PASSKEY_UPSELL_KEY);

    if (!storedTime || isStoredDateOlderThan30Days(storedTime)) {
      const response = await getListEnabledSecondFactors();

      if (response instanceof Error) {
        logEvent('show_passkey_guide_tour_error', {
          componentType: ComponentType.unknown,
          action: ActionType.process,
          error: response.message,
        });
        return;
      }

      if (!response.formFactors) {
        logEvent('show_passkey_guide_tour_error', {
          componentType: ComponentType.unknown,
          action: ActionType.process,
          error: 'formFactors is undefined',
        });
        return;
      }

      if (!isPasskeyEnabled(response.formFactors) && !isSecurityKeyEnabled(response.formFactors)) {
        localStorage.setItem(PASSKEY_UPSELL_KEY, Date.now().toString());
        // only request to check if business user when security key is disabled
        const isBusinessUser = await getIsBusinessUser();
        if (!isBusinessUser) {
          setShow(true);
        }
      }
    }
  }, [getIsBusinessUser, getListEnabledSecondFactors, logEvent]);

  const hideModel = useCallback(() => {
    if (show) {
      setShow(false);
    }
  }, [show]);

  useEffectOnce(() => {
    if (renderPasskeyGuideTourModal) {
      if (target === TwoFactorTargetEnv.Mock) {
        setTimeout(() => setShow(true), 2000);
      } else {
        void checkIfPasskeyGuideTourShouldBeShown();
      }
    }
  });

  return useMemo(() => ({ show, hideModel }), [hideModel, show]);
}
